import React, { useState } from 'react';
import { STITCH_TIERS } from '../data/stitchData';

export default function StrategicTiersSection({ onInspectTier, onScheduleClick }) {
  const [activeTierIndex, setActiveTierIndex] = useState(0);
  const [isGridView, setIsGridView] = useState(false);

  const tierKeys = ["tier-1", "tier-2", "tier-3"];
  const currentTierId = tierKeys[activeTierIndex];
  const currentTier = STITCH_TIERS[currentTierId];

  const handlePrev = () => {
    setActiveTierIndex((prev) => (prev - 1 + tierKeys.length) % tierKeys.length);
  };

  const handleNext = () => {
    setActiveTierIndex((prev) => (prev + 1) % tierKeys.length);
  };

  return (
    <section
      id="strategic-tiers"
      className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pb-16 my-8 rounded-3xl bg-gradient-to-b from-surface-container-low/90 via-surface-container-lowest to-surface-container-low/50 dark:from-clinical-slate-800/90 dark:via-clinical-slate-900 dark:to-clinical-slate-800/60 backdrop-blur-xl border border-glass-border-light dark:border-gray-800 shadow-sm overflow-hidden transition-colors duration-300"
    >
      <div className="relative z-10 flex flex-col space-y-8 pt-10">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center space-y-3 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-surface-container-low/80 dark:bg-clinical-slate-800/80 backdrop-blur-xl border border-glass-border-light dark:border-gray-700 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
            <span className="text-[11px] uppercase tracking-wider text-primary dark:text-primary-fixed font-bold">
              Strategic Advisory Architecture
            </span>
          </div>

          <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
            3 Strategic Tiers of Healthcare Governance
          </h2>

          <p className="text-sm sm:text-base text-gray-600 dark:text-slate-300 max-w-2xl leading-relaxed">
            Select a tier below to inspect targeted clinical practice protocols, verified impact metrics, and engagement models calibrated for healthcare leadership.
          </p>
        </div>

        {/* Bento Grid & Tabs Controller */}
        <div className="w-full space-y-6">
          {/* Tab Selector & Grid View Toggle */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-800 pb-3">
            <div className="flex flex-wrap items-center gap-2">
              {tierKeys.map((key, idx) => {
                const tier = STITCH_TIERS[key];
                const isActive = idx === activeTierIndex && !isGridView;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setActiveTierIndex(idx);
                      setIsGridView(false);
                    }}
                    className={`px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all duration-300 flex items-center gap-1.5 active:scale-95 ${
                      isActive
                        ? 'bg-primary text-white shadow-md'
                        : 'bg-surface-container-low dark:bg-clinical-slate-800 text-gray-600 dark:text-gray-300 hover:bg-surface-container-high dark:hover:bg-clinical-slate-700'
                    }`}
                  >
                    <span className="material-symbols-outlined text-[16px]">{tier.icon}</span>
                    <span>{tier.tabLabel}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              <button
                type="button"
                onClick={() => setIsGridView(!isGridView)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 dark:bg-clinical-slate-800 border border-gray-200 dark:border-gray-700 text-xs font-semibold text-primary dark:text-primary-fixed shadow-sm hover:bg-primary hover:text-white transition-all"
              >
                <span className="material-symbols-outlined text-[16px]">
                  {isGridView ? 'view_carousel' : 'grid_view'}
                </span>
                <span>{isGridView ? 'Slider View' : 'View All 3 Tiers'}</span>
              </button>
            </div>
          </div>

          {/* Conditional View: Slider or All 3 Tiers Bento Grid */}
          {!isGridView ? (
            /* Interactive Slider Card */
            <div className="relative w-full rounded-3xl bg-white/95 dark:bg-clinical-slate-900/90 backdrop-blur-xl border border-glass-border-light dark:border-gray-800 shadow-lg p-6 sm:p-10 overflow-hidden transition-all duration-500">
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary via-accent-teal to-secondary-container"></div>
              
              <div className="flex flex-col lg:flex-row gap-8 items-stretch">
                {/* Left Column: Tier Information */}
                <div className="flex-1 flex flex-col justify-between space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-ice-cyan/60 dark:bg-clinical-slate-800 border border-blue-100 dark:border-gray-700">
                        <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center">
                          <span className="material-symbols-outlined text-[18px]">
                            {currentTier.icon}
                          </span>
                        </div>
                        <span className="text-xs uppercase tracking-wider text-primary dark:text-primary-fixed font-bold">
                          {currentTier.badge}
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-2.5 py-0.5 rounded-full bg-surface-container dark:bg-clinical-slate-800">
                        Tier {activeTierIndex + 1} of 3
                      </span>
                    </div>

                    <div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                        {currentTier.title}
                      </h3>
                      <p className="text-sm font-semibold text-accent-teal mt-1">
                        {currentTier.subtitle}
                      </p>
                    </div>

                    <p className="text-sm sm:text-base text-gray-600 dark:text-slate-300 leading-relaxed">
                      {currentTier.overview}
                    </p>
                  </div>

                  {/* Core Clinical Practices Pills */}
                  <div className="space-y-2">
                    <span className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold block">
                      Core Clinical Practices ({currentTier.pillPractices.length})
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {currentTier.pillPractices.map((pill, pIdx) => (
                        <span
                          key={pIdx}
                          className="px-3 py-1 rounded-full text-xs font-medium bg-surface-container dark:bg-clinical-slate-800 text-gray-700 dark:text-slate-200 border border-gray-100 dark:border-gray-700"
                        >
                          {pill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-2 flex flex-wrap items-center gap-4">
                    <button
                      type="button"
                      onClick={() => onInspectTier(currentTierId)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white hover:bg-primary-container text-sm font-semibold shadow-md hover:scale-105 active:scale-95 transition-all"
                    >
                      <span className="material-symbols-outlined text-[18px]">open_in_full</span>
                      <span>Inspect Full Tier &amp; Protocols</span>
                    </button>

                    <button
                      type="button"
                      onClick={onScheduleClick}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary dark:text-primary-fixed hover:text-primary-container transition-colors"
                    >
                      <span>Request Tier Briefing</span>
                      <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                    </button>
                  </div>
                </div>

                {/* Right Column: Strategic ROI Box */}
                <div className="lg:w-80 flex flex-col justify-between p-6 rounded-2xl bg-surface-container-low/80 dark:bg-clinical-slate-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
                  <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-2">
                    <span className="text-xs uppercase tracking-wider text-primary dark:text-primary-fixed font-bold flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-accent-teal text-[18px]">trending_up</span>
                      Strategic ROI
                    </span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">Verified Impact</span>
                  </div>

                  <div className="space-y-3 flex-1 flex flex-col justify-around">
                    {currentTier.metrics.map((metric, mIdx) => (
                      <div
                        key={mIdx}
                        className="p-3.5 rounded-xl bg-white/90 dark:bg-clinical-slate-900/90 border border-gray-100 dark:border-gray-700 shadow-xs"
                      >
                        <span className="text-lg sm:text-xl font-bold text-primary dark:text-blue-300 block leading-tight">
                          {metric.val}
                        </span>
                        <span className="text-xs font-semibold text-gray-900 dark:text-white block mt-0.5">
                          {metric.label}
                        </span>
                        <span className="text-[11px] text-gray-500 dark:text-slate-400 block">
                          {metric.sub}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center italic">
                    *Historical engagement benchmarks; scope calibrated per institution.
                  </p>
                </div>
              </div>

              {/* Slider Controls Strip */}
              <div className="mt-8 pt-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="w-9 h-9 rounded-full bg-surface-container-low dark:bg-clinical-slate-800 hover:bg-primary hover:text-white transition-all flex items-center justify-center text-gray-700 dark:text-gray-200 shadow-xs active:scale-95"
                    aria-label="Previous Tier"
                  >
                    <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="w-9 h-9 rounded-full bg-surface-container-low dark:bg-clinical-slate-800 hover:bg-primary hover:text-white transition-all flex items-center justify-center text-gray-700 dark:text-gray-200 shadow-xs active:scale-95"
                    aria-label="Next Tier"
                  >
                    <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
                  </button>
                </div>

                <div className="flex-1 max-w-xs h-1.5 rounded-full bg-gray-200 dark:bg-clinical-slate-800 overflow-hidden mx-2">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${((activeTierIndex + 1) / tierKeys.length) * 100}%` }}
                  ></div>
                </div>

                <div className="flex items-center gap-2">
                  {tierKeys.map((_, dotIdx) => (
                    <button
                      key={dotIdx}
                      type="button"
                      onClick={() => setActiveTierIndex(dotIdx)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        dotIdx === activeTierIndex ? 'w-6 bg-primary' : 'w-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400'
                      }`}
                      aria-label={`Jump to Tier ${dotIdx + 1}`}
                    ></button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Bento Grid 3-Card Layout */
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
              {tierKeys.map((key) => {
                const tier = STITCH_TIERS[key];
                return (
                  <div
                    key={key}
                    onClick={() => onInspectTier(key)}
                    className="group relative p-6 rounded-2xl bg-white/90 dark:bg-clinical-slate-900/90 backdrop-blur-xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-lg hover:border-primary/40 transition-all duration-300 cursor-pointer flex flex-col justify-between"
                  >
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="w-12 h-12 rounded-2xl bg-ice-cyan dark:bg-clinical-slate-800 text-primary dark:text-primary-fixed flex items-center justify-center group-hover:scale-105 group-hover:bg-primary group-hover:text-white transition-all shadow-xs">
                          <span className="material-symbols-outlined text-[26px]">
                            {tier.icon}
                          </span>
                        </div>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary/80 dark:text-primary-fixed group-hover:text-primary transition-colors">
                          <span>Inspect</span>
                          <span className="material-symbols-outlined text-[18px]">open_in_full</span>
                        </span>
                      </div>

                      <div className="space-y-1.5">
                        <span className="text-[11px] uppercase tracking-wider text-primary dark:text-primary-fixed font-bold">
                          {tier.badge}
                        </span>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-primary transition-colors">
                          {tier.title}
                        </h3>
                        <p className="text-xs font-semibold text-accent-teal">
                          {tier.subtitle}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-slate-300 leading-relaxed pt-1">
                          {tier.overview}
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 mt-4 border-t border-gray-100 dark:border-gray-800">
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {tier.pillPractices.map((pill, pIdx) => (
                          <span
                            key={pIdx}
                            className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-surface-container dark:bg-clinical-slate-800 text-gray-600 dark:text-slate-300 border border-gray-100 dark:border-gray-700"
                          >
                            {pill}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between text-xs text-primary dark:text-primary-fixed font-semibold group-hover:translate-x-0.5 transition-transform">
                        <span>Tap to inspect Tier &amp; Services</span>
                        <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
